import { BBInfoItem } from '@/common/models/sows';
import { SPEED_INFO_CONTENT } from '@/common/utilities/settings';

export const SUPER_WIFI_TITLE = 'SuperWifi';
export enum BroadBandPackageName {
    mikro = 'Mikro',
    mini = 'Mini',
    midi = 'Midi',
    stofa_internet_300 = 'Internet 300 Mbit',
    maxi = 'Maxi',
    giga = 'Giga',
}

interface PackageProps {
    name: BroadBandPackageName;
    hasSafeSurf: boolean;
    hasSuperWifi: boolean;
    hasWifiGaranti: boolean;
}

export const SUPERWIFI_UNIT_CODES = {
    coax: ['SW99300-1', 'SW99300-2', 'SW99300-2', 'SW99300-2'],
    fiber: 'X22690',
};

const namesMapping: Record<string, Record<number, PackageProps>> = {
    coax: {
        30: {
            name: BroadBandPackageName.mikro,
            hasSafeSurf: true,
            hasSuperWifi: false,
            hasWifiGaranti: false,
        },
        100: {
            name: BroadBandPackageName.mini,
            hasSafeSurf: true,
            hasSuperWifi: false,
            hasWifiGaranti: false,
        },
        200: {
            name: BroadBandPackageName.midi,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: false,
        },
        300: {
            name: BroadBandPackageName.stofa_internet_300,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: false,
        },
        500: {
            name: BroadBandPackageName.maxi,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: false,
        },
        1000: {
            name: BroadBandPackageName.giga,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: true,
        },
    },
    fiber: {
        30: {
            name: BroadBandPackageName.mikro,
            hasSafeSurf: true,
            hasSuperWifi: false,
            hasWifiGaranti: false,
        },
        200: {
            name: BroadBandPackageName.midi,
            hasSafeSurf: true,
            hasSuperWifi: false,
            hasWifiGaranti: false,
        },
        500: {
            name: BroadBandPackageName.maxi,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: false,
        },
        1000: {
            name: BroadBandPackageName.giga,
            hasSafeSurf: true,
            hasSuperWifi: true,
            hasWifiGaranti: true,
        },
    },
};

export default class BroadBandPackage {
    public readonly product: BBInfoItem;
    private readonly packageProps: PackageProps;
    public readonly packageExists: boolean;

    constructor(props: BBInfoItem) {
        this.product = props;

        this.packageProps =
            namesMapping[this.product.technology_type][this.product.down_speed];

        this.packageExists = this.packageProps !== undefined;
    }

    get name(): string {
        if (this.packageProps) {
            return this.packageProps.name;
        } else {
            return '[udgår]';
        }
    }

    get displayName(): string {
        return `${this.name}${
            this.product.technology_type === 'fiber' ? ' Fiber' : ''
        }${this.hasWifiGaranti ? ' WifiGaranti' : ''}`;
    }

    get hasSafeSurf(): boolean {
        return this.packageProps ? this.packageProps.hasSafeSurf : false;
    }

    get hasSuperWifi(): boolean {
        return this.packageProps ? this.packageProps.hasSuperWifi : false;
    }

    get hasWifiGaranti(): boolean {
        return this.packageProps ? this.packageProps.hasWifiGaranti : false;
    }

    get about(): { title: string; content: any } {
        try {
            return {
                title: SPEED_INFO_CONTENT[this.product.down_speed].title,
                content: SPEED_INFO_CONTENT[this.product.down_speed].text,
            };
        } catch (e) {
            return { title: '', content: '' };
        }
    }

    getPriceOfSuperWifiUnitAtIndex(index: number): number {
        if (this.hasWifiGaranti) {
            return 0;
        } else if (!this.hasSuperWifi) {
            return 20;
        } else {
            if (index < 2) {
                return 0;
            } else {
                return 20;
            }
        }
    }

    getPriceOfSuperWifi(noOfUnits: number): number {
        if (this.hasWifiGaranti) {
            return 0;
        } else if (!this.hasSuperWifi) {
            return noOfUnits * 20;
        } else {
            if (noOfUnits < 2) {
                return 0;
            } else {
                return (noOfUnits - 2) * 20;
            }
        }
    }

    getOrderCodeOfSuperWifiUnitAtIndex(index: number): string {
        return this.product.technology_type === 'fiber'
            ? SUPERWIFI_UNIT_CODES.fiber
            : SUPERWIFI_UNIT_CODES.coax[index];
    }
}
